import { Entity, formatTicker } from '@api';
import { Button } from '@design/components/button/Button';
import { EntityDetails } from '@design/widget-modules/entity-details/EntityDetails';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import { TruncateTooltip } from '@terminal/components/truncate-tooltip/TruncateTooltip';
import { WatchlistTrigger } from '@terminal/components/watchlist/watchlist-trigger/WatchlistTrigger';
import { WebOnlyFeat } from '@terminal/config/WebOnlyFeat';
import clsx from 'clsx';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './EntityHeader.module.scss';

export interface EntityHeaderProps {
  className?: string;
  entity: Entity;
  shouldShowLogo?: boolean;
  priceDisplayNode?: ReactNode;
  isCondensed?: boolean;
  truncateLongTitle?: boolean;
  truncateAtLength?: number;
  headerButton?: ReactNode;
  isDrawer?: boolean;
  rightNode?: ReactNode;
  hideButtonNode?: boolean;
}

const DEFAULT_ICON_SIZE = 48;
const SMALL_ICON_SIZE = 20;

export const EntityHeader = ({
  className,
  entity,
  shouldShowLogo = true,
  priceDisplayNode,
  isCondensed = false,
  truncateLongTitle = false,
  truncateAtLength = 10,
  headerButton,
  isDrawer = false,
  rightNode,
  hideButtonNode = false,
}: EntityHeaderProps) => {
  const { t } = useTranslation('common');
  const ticker = formatTicker(entity);

  const entityName = isCondensed
    ? ticker
    : entity.name_short || entity.name_full;

  const getWatchlistLabel = (isInWatchlist: boolean) => {
    if (headerButton) {
      return undefined;
    }

    return isInWatchlist
      ? t('common:components.watchlistBtn.watching')
      : t('common:components.watchlistBtn.addToWatchlistLabel');
  };

  return (
    <EntityDetails
      className={clsx(className, styles.styledEntityDetails, {
        [styles.condensed]: isCondensed,
      })}
      isCondensed={isCondensed}
      entityNameComponent={
        truncateLongTitle ? (
          <TruncateTooltip
            placement="top"
            atLength={truncateAtLength}
            text={entityName}
            data-testid="analyze-header-title"
          />
        ) : (
          <span data-testid="text">{entityName}</span>
        )
      }
      priceDisplayNode={priceDisplayNode}
      formattedTicker={isCondensed ? '' : ticker}
      leftImgNode={
        shouldShowLogo && (
          <AssetLogoContainer
            className={clsx(styles.styledAssetLogoContainer, {
              [styles.condensed]: isCondensed,
              [styles.drawer]: isDrawer,
            })}
            size={isCondensed ? SMALL_ICON_SIZE : DEFAULT_ICON_SIZE}
            entity={entity}
          />
        )
      }
      rightNode={rightNode}
      buttonNode={
        !hideButtonNode && (
          <div className={styles.buttonContainer}>
            {headerButton}
            <WebOnlyFeat>
              <WatchlistTrigger
                tag={entity.tag}
                dropdownProps={{
                  strategy: 'fixed',
                  placement: 'top-start',
                  inPortal: false,
                }}
                renderTrigger={isInWatchlist => (
                  <Button
                    className={clsx(styles.watchlistButton, {
                      [styles.active]: isInWatchlist,
                      [styles.roundIcon]: !!headerButton,
                    })}
                    data-testid="watchlist-button"
                    label={getWatchlistLabel(isInWatchlist)}
                    size="small"
                    variant="tertiary"
                    iconName={isInWatchlist ? 'Checkmark' : 'Add'}
                  />
                )}
              />
            </WebOnlyFeat>
          </div>
        )
      }
    />
  );
};
